import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { JsonInput } from '@shared/components/JsonInput';
import {
  McpToolPopupProps,
  McpToolFormData,
  McpToolJsonData,
  MCP_JSON_TEMPLATE,
  getEmptyMcpToolFormData,
  headersArrayToObject,
  jsonToFormData,
  validateMcpJson,
} from './mcp.types';
import McpToolForm from './McpToolForm';
import TabbedEditorDialog from './TabbedEditorDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 1 }}>
    {value === index && children}
  </Box>
);

const McpToolPopup: React.FC<McpToolPopupProps> = ({
  open,
  onClose,
  initialData,
  initialRawJson,
  agents,
  existingTools,
  onSubmit,
  onSubmitRawJson,
  isLoading,
  disabled,
  fixedAgentId,
}) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<McpToolFormData>(
    initialData || getEmptyMcpToolFormData(),
  );
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const originalRawConfigRef = useRef<Record<string, unknown> | null>(null);
  const isUpdatingFromFormRef = useRef(false);
  const isUpdatingFromJsonRef = useRef(false);

  const isEditing = Boolean(initialData?.id);

  const buildJsonFromFormData = useCallback((data: McpToolFormData): McpToolJsonData => {
    const headersObj = headersArrayToObject(data.mcpServerConfig.headers);

    const mcpServerConfig: Record<string, unknown> = {
      ...(originalRawConfigRef.current || {}),
      url: data.mcpServerConfig.url,
      headers: headersObj,
      transportType: data.mcpServerConfig.transportType,
    };

    return {
      mcpName: data.mcpName,
      agentId: data.agentId,
      mcpServerConfig: mcpServerConfig as McpToolJsonData['mcpServerConfig'],
    };
  }, []);

  useEffect(() => {
    if (open) {
      const data = initialData || getEmptyMcpToolFormData();
      // Set agentId from fixedAgentId if provided and not already set
      if (fixedAgentId !== undefined && !initialData) {
        data.agentId = fixedAgentId;
      }
      setFormData(data);

      if (initialRawJson) {
        originalRawConfigRef.current =
          (initialRawJson.mcpServerConfig as Record<string, unknown>) || null;
        setJsonValue(JSON.stringify(initialRawJson, null, 2));
      } else if (initialData) {
        originalRawConfigRef.current = null;
        setJsonValue(JSON.stringify(buildJsonFromFormData(initialData), null, 2));
      } else {
        originalRawConfigRef.current = null;
        const template = { ...MCP_JSON_TEMPLATE };
        // Set agentId in JSON template if fixedAgentId is provided
        if (fixedAgentId !== undefined) {
          template.agentId = fixedAgentId;
        }
        setJsonValue(JSON.stringify(template, null, 2));
      }

      setJsonError(null);
      setActiveTab(0);
      isUpdatingFromFormRef.current = false;
      isUpdatingFromJsonRef.current = false;
    }
  }, [open, initialData, initialRawJson, buildJsonFromFormData, fixedAgentId]);

  useEffect(() => {
    if (activeTab === 1 && !isUpdatingFromJsonRef.current) {
      isUpdatingFromFormRef.current = true;
      setJsonValue(JSON.stringify(buildJsonFromFormData(formData), null, 2));
      setTimeout(() => {
        isUpdatingFromFormRef.current = false;
      }, 0);
    }
  }, [formData, activeTab, buildJsonFromFormData]);

  useEffect(() => {
    if (activeTab === 1 && jsonValue && !isUpdatingFromFormRef.current) {
      try {
        const parsed = JSON.parse(jsonValue);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          isUpdatingFromJsonRef.current = true;
          const jsonData = parsed as McpToolJsonData;
          const newFormData = jsonToFormData(jsonData, formData.id);
          setFormData(newFormData);
          if (jsonData.mcpServerConfig) {
            originalRawConfigRef.current =
              (jsonData.mcpServerConfig as Record<string, unknown>) || null;
          }
          setTimeout(() => {
            isUpdatingFromJsonRef.current = false;
          }, 0);
        }
      } catch {}
    }
  }, [jsonValue, activeTab, formData.id]);

  const handleTabChange = (newValue: number) => {
    if (newValue === 1) {
      setJsonError(null);
    } else if (newValue === 0 && activeTab === 1) {
      const validation = validateMcpJson(jsonValue);
      if (validation.valid && validation.data) {
        originalRawConfigRef.current =
          (validation.data.mcpServerConfig as Record<string, unknown>) || null;
        isUpdatingFromJsonRef.current = true;
        setFormData(jsonToFormData(validation.data, formData.id));
        setTimeout(() => {
          isUpdatingFromJsonRef.current = false;
        }, 0);
      }
    }
    setActiveTab(newValue);
  };

  const handleFormSubmit = async (data: McpToolFormData) => {
    try {
      // Ensure fixedAgentId is used when in agent single mode
      const dataToSubmit = fixedAgentId !== undefined ? { ...data, agentId: fixedAgentId } : data;
      await onSubmit(dataToSubmit);
      setFormData(getEmptyMcpToolFormData());
      setJsonValue(JSON.stringify(MCP_JSON_TEMPLATE, null, 2));
      originalRawConfigRef.current = null;
    } catch {}
  };

  const handleJsonSubmit = async () => {
    const validation = validateMcpJson(jsonValue);
    if (!validation.valid) {
      setJsonError(validation.error || 'Invalid JSON');
      return;
    }

    if (validation.data) {
      const trimmedName = validation.data.mcpName.trim();
      // Use fixedAgentId when in agent single mode, otherwise use the value from JSON
      const currentAgentId =
        fixedAgentId !== undefined ? fixedAgentId : validation.data.agentId || null;

      // Check if there's a tool with the same name and same binding (agentId)
      const duplicateTool = existingTools.find(
        (tool) =>
          tool.mcpName.trim() === trimmedName &&
          (tool.agentId || null) === currentAgentId &&
          tool.id !== initialData?.id,
      );

      if (duplicateTool) {
        setJsonError(t('mcpTools.validation.uniqueName'));
        return;
      }

      try {
        await onSubmitRawJson({
          id: formData.id,
          mcpName: validation.data.mcpName,
          agentId: currentAgentId,
          mcpServerConfig: validation.data.mcpServerConfig as Record<string, unknown>,
        });
        setFormData(getEmptyMcpToolFormData());
        setJsonValue(JSON.stringify(MCP_JSON_TEMPLATE, null, 2));
        originalRawConfigRef.current = null;
      } catch {}
    }
  };

  const handleJsonChange = (value: string) => {
    setJsonValue(value);
    if (jsonError) {
      setJsonError(null);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <TabbedEditorDialog
      open={open}
      onClose={handleClose}
      title={isEditing ? t('mcpTools.editTool') : t('mcpTools.newTool')}
      tabs={[
        { label: t('mcpTools.tabDetails'), disabled: isLoading },
        { label: t('mcpTools.tabJson'), disabled: isLoading },
      ]}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      disableClose={isLoading}
      paperSx={{ minHeight: '500px' }}
      actions={
        activeTab === 1 ? (
          <>
            <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleJsonSubmit}
              disabled={disabled || isLoading}
              sx={{ minWidth: 140 }}>
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditing ? (
                t('mcpTools.updateTool')
              ) : (
                t('mcpTools.saveTool')
              )}
            </Button>
          </>
        ) : undefined
      }>
      <TabPanel value={activeTab} index={0}>
        <McpToolForm
          initialData={formData}
          agents={agents}
          existingTools={existingTools}
          onSubmit={handleFormSubmit}
          onChange={setFormData}
          onCancel={handleClose}
          isLoading={isLoading}
          disabled={disabled}
          fixedAgentId={fixedAgentId}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('mcpTools.jsonEditorHelp')}
          </Typography>

          <JsonInput
            value={jsonValue}
            onChange={handleJsonChange}
            error={jsonError}
            rows={16}
            disabled={disabled || isLoading}
          />
        </Box>
      </TabPanel>
    </TabbedEditorDialog>
  );
};

export default McpToolPopup;
